import { fetchUsersForFilter } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { SEARCH_ICON } from 'constants/filterIcons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUsersList, getUsersLoading } from 'selectors/users';
import 'styles/labelsStyle.css';
import type { Role, User } from 'types';

type UserSelectProps = {
  selectUser: (user: User, checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  selectedUsers?: User[];
  roles?: Role[];
  defaultOpened?: boolean;
};

const UserSelect: React.FC<UserSelectProps> = (props) => {
  const {
    selectUser,
    disabled,
    selectedUsers = [],
    roles,
    className = '',
    defaultOpened = false,
  } = props;

  const dispatch = useDispatch();
  const users = useSelector(getUsersList);
  const usersLoading = useSelector(getUsersLoading);
  const [isOpen, setIsOpen] = useState(defaultOpened);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const handleClickOutside = useCallback(() => {
    setSearch('');
    setIsOpen(false);
  }, [setIsOpen]);

  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, handleClickOutside);

  const handleButtonClick = (): void => {
    setIsOpen(true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, user: User): void => {
    selectUser(user, event.target.checked);

    event.stopPropagation();
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return (): void => {
      clearTimeout(t);
    };
  }, [search]);

  useEffect(() => {
    dispatch(fetchUsersForFilter({ searchValue: debouncedSearch, roles }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, dispatch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e?.target.value.toLowerCase();
    setSearch(value);
  };

  const renderOptions = (): JSX.Element => {
    if (usersLoading) return <LoadingIndicator />;

    const filteredUsers = users
      .filter((u) => !selectedUsers.find((su) => su.uuid === u.uuid))
      .sort();

    const filteredSelectedUsers = selectedUsers.filter((u) =>
      u.name?.toLowerCase()?.includes(search.toLowerCase())
    );

    return (
      <fieldset className="m-2 flex flex-col gap-2" data-testid="users-list">
        {filteredSelectedUsers.length > 0 || filteredUsers.length > 0 ? (
          [...filteredSelectedUsers, ...filteredUsers].map((user, idx) => (
            <div key={user.uuid} data-testid={`user-option-${idx}`} className="cursor-pointer">
              <label
                htmlFor={`user-option-${user.uuid}`}
                className="flex flex-row items-center gap-1"
              >
                <input
                  type="checkbox"
                  id={`user-option-${user.uuid}`}
                  data-testid={`user-select-button-${idx}`}
                  onChange={(e): void => handleInputChange(e, user)}
                  checked={selectedUsers?.some((u) => u.uuid === user.uuid)}
                  className="form-checkbox litlingo-checkbox"
                />

                <div className="text-small select-none break-all">{user.name}</div>
              </label>
            </div>
          ))
        ) : (
          <div className="text-small">No users found</div>
        )}
      </fieldset>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className={`relative inline-block text-left w-full ${className || ''} rounded`}
    >
      <div className="rounded shadow-sm">
        <div className="flex flex-row items-center w-full h-8 bg-white rounded">
          <div className="absolute left-0 ml-1.5 w-5">{SEARCH_ICON()}</div>
          <input
            id="search"
            name="search"
            disabled={disabled}
            data-testid="user-select-button"
            className="form-input h-full w-full text-litlingo-gray-6 pl-8 text-body placeholder-italic placeholder-litlingo-gray-6"
            value={search}
            placeholder="Search Reviewers"
            onChange={handleSearchChange}
            onFocus={handleButtonClick}
            autoComplete="off"
          />
        </div>
      </div>

      {isOpen && (
        <div className="origin-top-left absolute left-0 mt-1 w-full rounded-md shadow-lg z-40">
          <div className="rounded-md bg-white shadow-xs">
            <div
              className="py-1"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="options-menu"
            >
              <div className="max-h-select-dropdown overflow-auto custom-scrollbar-select-dropdown">
                {renderOptions()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSelect;
